<template>
    <section v-if="isFilledContentRelationship(slice.primary.relatedcontent)" class="related-content-slice">
        <MktSliceTitle v-if="slice.primary.title" :field="slice.primary.title" class="title" />
        <NuxtLink :to="linkResolver(slice.primary.relatedcontent)" class="related-content">
            <div class="thumbnail-wrapper">
                <PrismicImage
                    v-if="hasProperty(slice.primary.relatedcontent.data, 'thumbnail')"
                    :class="{
                        thumbnail: true,
                        cropped: slice.primary.relatedcontent.type === 'article',
                        centered: slice.primary.relatedcontent.type === 'guide',
                    }"
                    :field="slice.primary.relatedcontent.data?.thumbnail"
                />
            </div>
            <div class="content-info">
                <div class="top-row">
                    <!-- <CategoryPill class="category-pill" :category="slice.primary.relatedcontent.data?.category ?? null" /> -->
                    <div v-if="slice.primary.relatedcontent.type !== 'guide'" class="time-info">
                        <template v-if="hasProperty(slice.primary.relatedcontent.data, 'releaseDate')">
                            <Calendar class="icon" />
                            <span>
                                {{
                                    new Date(slice.primary.relatedcontent.data?.releaseDate as string).toLocaleDateString(locale, {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                    })
                                }}
                            </span>
                        </template>
                    </div>
                </div>
                <h4 class="related-content-title">{{ slice.primary.relatedcontent.data?.title }}</h4>

                <PrismicRichText class="excerpt" :field="slice.primary.relatedcontent.data?.excerpt" />
            </div>
        </NuxtLink>
    </section>
</template>

<script setup lang="ts">
    import {inject} from 'vue';
    import {getSliceComponentProps, PrismicImage, PrismicRichText} from '@prismicio/vue';
    import type {RelatedContentSlice} from '@prismicio-types';
    import {MktSliceTitle} from '#components';
    import {createContentHelpers, usePrismicTypeGuards} from '#imports';
    import {USER_LOCALE} from '~/app/keys';
    import linkResolver from '~/app/prismic/link-resolver';
    import Calendar from '~/components/icons/Calendar.vue';

    defineProps(getSliceComponentProps<RelatedContentSlice>());

    const {isFilledContentRelationship} = createContentHelpers();
    const locale = inject(USER_LOCALE);
    const {hasProperty} = usePrismicTypeGuards();
</script>

<style lang="scss">
    @use '~/assets/styles/variables' as *;

    .related-content-slice {
        margin-top: var(--joy-core-spacing-11);
        margin-bottom: var(--joy-core-spacing-11);

        @include content-width($article-max-width);

        .title h2 {
            margin-bottom: var(--joy-core-spacing-7);
        }

        .related-content {
            background-color: var(--joy-color-quaternary-10);
            display: flex;
            align-items: stretch;

            @media (max-width: $mobile-breakpoint) {
                flex-direction: column;
            }

            .thumbnail-wrapper {
                flex-basis: 40%;
            }

            .thumbnail {
                object-fit: contain;
                width: 100%;
                height: 100%;

                &.cropped {
                    object-fit: cover;
                }

                &.centered {
                    @media (max-width: $mobile-breakpoint) {
                        align-self: center;
                        max-height: 100%;
                        max-width: 100%;
                        height: auto;
                        width: auto;
                        margin-top: var(--joy-core-spacing-5);
                    }
                }
            }

            .content-info {
                display: flex;
                flex-direction: column;
                justify-content: center;
                padding: var(--joy-core-spacing-5) var(--joy-core-spacing-9);
                flex-basis: 60%;

                @media (max-width: $mobile-breakpoint) {
                    padding: var(--joy-core-spacing-4) var(--joy-core-spacing-7) var(--joy-core-spacing-9);
                }

                .top-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;

                    .category-pill:not(:hover) {
                        background-color: var(--joy-color-quaternary-10);
                    }

                    .time-info {
                        color: var(--joy-color-neutral-40);

                        .icon {
                            margin-right: var(--joy-core-spacing-2);
                        }
                    }
                }

                .related-content-title {
                    margin-top: var(--joy-core-spacing-5);
                    margin-bottom: var(--joy-core-spacing-3);
                }

                .excerpt {
                    p {
                        color: var(--joy-color-neutral-40);
                    }
                }
            }
        }

        /* Themes */
        .resource-page--corporate &,
        .resource-page--strategy & {
            .title h2 {
                color: var(--joy-color-secondary-50);
            }

            .related-content {
                background-color: var(--joy-color-neutral-20);
            }
        }
    }
</style>
